import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Modal,
  MultiCombobox,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import type {
  CampaignInclusion,
  MasterRef,
  ProductInclusionWriteEntry,
  ReferenceRecord,
} from '@/lib/types';

/**
 * Which campaign inclusions ONE PRODUCT offers.
 *
 * The product<->inclusion link table is EMPTY server-side, so the quote-line
 * inclusions editor is hidden on every one of the 28 products until this
 * dialog writes to it — the feature is unreachable, not unconfigured. This
 * is the only screen that can populate that table.
 *
 * Assigns which inclusions a product OFFERS, not quantities — a quantity
 * belongs to a line item, not the product, so there's deliberately no
 * quantity control here (see `src/lib/inclusions.ts`).
 *
 * Contract:
 *   GET  /api/v1/products/{id}/inclusions   any authenticated role
 *   PUT  /api/v1/products/{id}/inclusions   ADMIN_ROLES
 *        body { inclusionIds: Entry[] }     REQUIRED — absent is a 422
 *                                           [] deliberately CLEARS
 *        Entry = number | {inclusionId, regionId?}
 *
 * ## The REGION SPLIT — migration 096, release `20260829123506-c323947`
 *
 * A product may offer an inclusion **only in some of SEED's regions**: the
 * owner's example is a digital-advertising campaign whose *Influencer Campaign*
 * add-on runs in India and nowhere else. This dialog is where that is set.
 *
 * ⚠️ **It is a FILTER, not a LABEL.** The region decides which inclusions a
 * LINE may choose — the backend refuses the rest with
 * `422 INCLUSION_NOT_OFFERED_IN_REGION` — and the chosen inclusion then prints
 * on the customer's document **exactly as it prints today**. `(india only)` is
 * the administrator's note about the offer and must never reach a composed line
 * description. Nothing here writes into `line-description.ts` or
 * `inclusions.ts`, and nothing should start to.
 *
 * ⚠️ **`offeredInRegions: null` means EVERYWHERE, and it is the OPPOSITE of
 * `[]`.** `null` is the widest possible offer; an empty array reads as "offered
 * nowhere". **Never `?? []`** and never count `.length` without testing `null`
 * first — a coalesce turns the default offer into no offer at all, silently.
 * {@link scopesOf} is the one place this file reads the field, and
 * {@link offeredNowhere} is what it does with an `[]` it is not supposed to be
 * able to receive.
 *
 * ⚠️ **The write has NO `null` spelling for "clear the region".** It is a full
 * replace, so *everywhere* is expressed by sending the entry as a **bare id** —
 * which is exactly what a bare id has always meant, so the shape is purely
 * additive. One inclusion in two regions is the same id sent twice with two
 * `regionId`s. {@link buildOffers} is the single translation point.
 *
 * ⚠️ **An unknown or RETIRED `regionId` is a 422 naming `inclusionIds`** —
 * stricter than the inclusion ids themselves. Hence `?isActive=true` on the
 * regions read. There are six regions and all six are active today, so that
 * filter narrows nothing and omitting it looks correct; it breaks the first time
 * anybody retires one.
 *
 * ⚠️ **The region match is a UNION, and the split is a DEFAULT rather than a
 * GUARANTEE.** On a project spanning India and Africa an India-only inclusion
 * IS offered, and nothing downstream asserts the work happens in India.
 * Intersection and outright refusal were both put to the owner and both
 * declined — so no copy here may imply the region is enforced per city or per
 * delivery.
 *
 * ⚠️ **Nothing is retroactive.** Inclusions already recorded on a quote, invoice
 * or deal line go on printing what they print today; the split governs what a
 * picker OFFERS on a new write.
 *
 * - Write is `ADMIN_ROLES` (the reference-data tier, same as
 *   `/campaign-inclusions` and `/products`), so `advanced_user` accounts get
 *   a read-only dialog gated on `ability.canUpdate('products')` — never on
 *   the role directly or on `/permissions/me`, which over-reports this tier.
 * - `requireRole` runs BEFORE `validate`, so a non-admin 403 carries no
 *   `details` and must render as a form banner, never field errors.
 * - ⚠️ The GET's query schema is an EMPTY strict object: `?isActive=true` on
 *   `/products/{id}/inclusions` is a 422 naming the key, unlike
 *   `/campaign-inclusions` where that same param is required to exclude
 *   retired rows. Both calls are below — note which is bare.
 */

/**
 * The minimum this dialog needs about the product. Deliberately not the whole
 * `Product`, so a caller holding a list row or a detail record can both mount
 * it without either one being converted first.
 */
export interface ProductInclusionsTarget {
  id: number;
  productName: string;
}

export interface ProductInclusionsModalProps {
  open: boolean;
  /** `null` renders nothing — the caller may leave the prop wired while closed. */
  product: ProductInclusionsTarget | null;
  onClose: () => void;
  /** Fired after a successful save, before `onClose`. Refresh the caller's list here. */
  onSaved: () => void;
}

/** The list is short today (4 rows) but the master's ceiling is 200. */
const MASTER_PAGE_SIZE = 200;

/** `/regions` caps here too. Six rows today; the ceiling costs nothing. */
const REGION_PAGE_SIZE = 200;

/** How many skeleton rows to draw. Enough to look like a list, not a page. */
const SKELETON_ROWS = 5;

/**
 * Which regions ONE inclusion is offered in, per inclusion id.
 *
 * ⚠️ **A key that is ABSENT means EVERYWHERE.** That is the draft's spelling of
 * the wire's `offeredInRegions: null` and of a bare id in the write body, and it
 * is the default every existing row is in.
 *
 * ⚠️ **A key is never present with an EMPTY array.** "Offered in no region" is
 * not expressible in the write body — there is no `regionId: null` — and it is
 * not a state the UI can produce either: emptying the region control means
 * *everywhere*, which drops the key. {@link setScope} is the only writer and it
 * enforces that, so no code downstream has to ask whether `[]` means "all" or
 * "none". The two-opposite-meanings-for-one-falsy-value trap this repo keeps
 * finding is closed by construction rather than by a convention somebody has to
 * remember.
 */
type RegionScopes = ReadonlyMap<number, readonly number[]>;

/**
 * The wire's `offeredInRegions` for one product, as {@link RegionScopes} plus
 * the two things reading it can turn up that the contract says cannot happen.
 *
 * Returned as one object rather than three reads so the three can never
 * disagree about the same response.
 */
interface ScopeRead {
  scopes: RegionScopes;
  /**
   * Regions named on the wire, by id — including any the `?isActive=true` list
   * does not carry.
   *
   * This is the retired-value-injection rule: a read never filters on
   * `isActive` while a write refuses a retired row, so a region already recorded
   * against an inclusion must still render with its NAME. Dropping it would show
   * a bare id, and filtering it out of the draft would silently widen the offer
   * to everywhere on the next save.
   */
  seen: ReadonlyMap<number, MasterRef>;
  /**
   * Inclusions the server reported as offered in **no** region — `[]` rather
   * than `null`.
   *
   * ⚠️ **Not defensive padding.** `[]` and `null` are opposites on this field
   * and the whole hazard is that a client silently reads one as the other, so
   * the impossible case is recorded rather than coalesced. It is surfaced as a
   * warning naming what saving will do, because the only state the write body
   * can express for such a row is *everywhere* — and widening an offer without
   * saying so is exactly the silent substitution the field's own contract note
   * warns about.
   */
  offeredNowhere: ReadonlySet<number>;
}

/**
 * Read `offeredInRegions` off a GET response, once.
 *
 * ⚠️ **The field is optional on the type** (`MasterRef[] | null | undefined`)
 * because it is present only on this endpoint, not on the campaign-inclusions
 * master. `undefined` — a server predating migration 096 — reads as EVERYWHERE,
 * which is what every row meant before the split existed. That is the one
 * coalesce in this file and it is deliberate: it restores the pre-096 meaning
 * rather than inventing a narrower one.
 */
function scopesOf(offered: readonly CampaignInclusion[]): ScopeRead {
  const scopes = new Map<number, readonly number[]>();
  const seen = new Map<number, MasterRef>();
  const offeredNowhere = new Set<number>();

  for (const inclusion of offered) {
    const regions = inclusion.offeredInRegions;
    // `null` and `undefined` both mean everywhere. Tested explicitly, never
    // through truthiness or `??`, because `[]` is the opposite answer.
    if (regions === null || regions === undefined) continue;
    if (regions.length === 0) {
      offeredNowhere.add(inclusion.id);
      continue;
    }
    for (const region of regions) seen.set(region.id, region);
    scopes.set(
      inclusion.id,
      regions.map((region) => region.id),
    );
  }

  return { scopes, seen, offeredNowhere };
}

/**
 * The draft as a `PUT` body — the ONE place "everywhere" becomes a bare id.
 *
 * Order follows `selectedIds`, and a multi-region inclusion is emitted once per
 * region, which is the only spelling the contract offers.
 */
function buildOffers(selectedIds: readonly number[], scopes: RegionScopes): ProductInclusionWriteEntry[] {
  const offers: ProductInclusionWriteEntry[] = [];
  for (const inclusionId of selectedIds) {
    const regionIds = scopes.get(inclusionId);
    /*
     * ⚠️ Absent OR empty both emit a BARE ID. `setScope` guarantees empty never
     * reaches here, and the second half of the test is a backstop rather than a
     * second meaning: `{inclusionId, regionId: undefined}` would be an object
     * whose `regionId` key the `.strict()` schema may or may not forgive, and a
     * bare id is the shape the contract documents for everywhere.
     */
    if (regionIds === undefined || regionIds.length === 0) {
      offers.push(inclusionId);
      continue;
    }
    for (const regionId of regionIds) offers.push({ inclusionId, regionId });
  }
  return offers;
}

/**
 * The draft's region assignment as one comparable string, over the SELECTED
 * inclusions only.
 *
 * Scoped to the selection because an unticked inclusion sends nothing, so a
 * region left behind on one must not read as an unsaved change — the Save
 * button would enable itself over a difference the PUT cannot express.
 */
function scopeKey(selectedIds: readonly number[], scopes: RegionScopes): string {
  return selectedIds
    .map((id) => `${id}:${[...(scopes.get(id) ?? [])].sort((a, b) => a - b).join('.')}`)
    .join('|');
}

/**
 * What went wrong on save, said in full.
 *
 * ⚠️ Deliberately NOT `error.formMessage ?? error.message` — both can be true
 * at once (a generic "Validation failed" plus a specific detail), and
 * coalescing would silently drop one. Everything the server said is
 * rendered, de-duplicated.
 */
function describeSaveFailure(error: ApiError, productName: string): string {
  if (error.status === 403) {
    return 'Nothing was saved — changing this is an administrator action.';
  }
  if (error.status === 404) {
    return `Nothing answered for ${productName}. Close this dialog and reload the list.`;
  }

  const lines: string[] = [error.message];
  for (const message of error.formMessages) {
    if (!lines.includes(message)) lines.push(message);
  }
  for (const [path, message] of Object.entries(error.fieldErrors)) {
    // Only one write key exists, so a path-keyed detail has no field of its
    // own — it belongs in the banner with its path named.
    const line = `${path}: ${message}`;
    if (!lines.includes(line)) lines.push(line);
  }
  return lines.join(' — ');
}

/**
 * One inclusion: the tick, and — when it is ticked — the regions it is offered
 * in.
 *
 * ⚠️ **The region control is only rendered on a TICKED row**, because a region
 * assignment on an inclusion the product does not offer is not a state the write
 * body can carry. It is not *cleared* when the row is unticked — see
 * {@link ProductInclusionsModal}'s `toggle` — so re-ticking restores the choice
 * rather than silently widening the offer to everywhere.
 *
 * ⚠️ **An EMPTY selection means EVERY region**, which is the default and the
 * pre-096 meaning of every existing row. The picker states that as its value
 * (`All regions`) rather than explaining it in a sentence: it is what the
 * control currently holds, not advice, so it is not helper text and it cannot
 * drift from the payload the way a sentence could.
 */
function OfferRow({
  inclusion,
  label,
  checked,
  disabled,
  regionIds,
  regionOptions,
  regionsDisabled,
  onToggle,
  onRegionsChange,
}: {
  inclusion: CampaignInclusion;
  label: string;
  checked: boolean;
  disabled: boolean;
  /** ⚠️ `undefined` means EVERYWHERE. It is never an empty array. */
  regionIds: readonly number[] | undefined;
  regionOptions: { value: string; label: string }[];
  regionsDisabled: boolean;
  onToggle: (next: boolean) => void;
  onRegionsChange: (regionIds: number[]) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Checkbox
        label={label}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onToggle(event.target.checked)}
      />
      {checked && (
        <div className="pl-6">
          <Field
            // Named per inclusion so a screen reader hears which offer this
            // narrows; hidden because the row above already says it on screen.
            label={`Regions ${inclusion.name} is offered in`}
            hideLabel
          >
            {(fieldProps) => (
              <MultiCombobox
                {...fieldProps}
                values={(regionIds ?? []).map(String)}
                onChange={(values) => onRegionsChange(values.map(Number))}
                options={regionOptions}
                placeholder="All regions"
                searchPlaceholder="Filter regions"
                disabled={regionsDisabled}
                summaryNoun={{ singular: 'region', plural: 'regions' }}
                // Six regions, and "offered everywhere" is the common answer —
                // the case the row exists for.
                selectAll
              />
            )}
          </Field>
        </div>
      )}
    </div>
  );
}

export function ProductInclusionsModal({
  open,
  product,
  onClose,
  onSaved,
}: ProductInclusionsModalProps) {
  const { ability } = useAuth();
  const canWrite = ability.canUpdate('products');
  const readOnlyReason = canWrite ? null : denialReason(ability, 'update', 'products');

  /*
   * Which product this dialog has finished a read for — success or failure.
   * `pending` is DERIVED from this during render rather than a separate flag
   * flipped in the effect, so a product swap on a mounted dialog shows
   * loading instantly instead of briefly rendering the previous product's
   * inclusions as though they were this one's.
   */
  const [settledFor, setSettledFor] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  /** The pickable set: `/campaign-inclusions?isActive=true`, in `sortOrder, name`. */
  const [master, setMaster] = useState<CampaignInclusion[]>([]);
  /** How many active rows exist server-side, so a truncated page can say so. */
  const [masterTotal, setMasterTotal] = useState(0);
  /** What this product offers right now, straight from the GET. */
  const [offered, setOffered] = useState<CampaignInclusion[]>([]);
  /** The draft. The whole of it is what a save sends. */
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());

  /**
   * The region assignment: draft, and the baseline the save compares against.
   *
   * Kept beside `selected` rather than folded into it because they are two
   * independent edits with two independent refusals — an unknown inclusion id is
   * forgiven if the product already offers it, while an unknown or retired
   * REGION id is refused outright. One map carrying both would make the
   * comparison in `dirty` guess which half moved.
   */
  const [scopes, setScopes] = useState<RegionScopes>(new Map());
  const [loadedScopes, setLoadedScopes] = useState<RegionScopes>(new Map());
  /** ⚠️ `[]` on the wire, which the contract says cannot happen. See {@link ScopeRead}. */
  const [offeredNowhere, setOfferedNowhere] = useState<ReadonlySet<number>>(new Set());

  /** `/regions?isActive=true`, in `sortOrder, name`. */
  const [regions, setRegions] = useState<ReferenceRecord[]>([]);
  /**
   * The regions read failed.
   *
   * ⚠️ **This DISABLES the region controls; it never renders an empty list.**
   * With no options the only reachable outcome is emptying a scope, which means
   * *everywhere* — so an unread list would let somebody silently widen an offer
   * they could not see. The stored assignment is left exactly as it loaded and
   * the inclusion ticks stay editable, because those are a different request's
   * data and a different refusal.
   */
  const [regionsFailed, setRegionsFailed] = useState(false);
  /**
   * Regions the response named that `?isActive=true` did not return — retired,
   * or simply absent. Injected back into the options so a stored assignment
   * renders as a NAME, exactly as the retired-inclusion block below does.
   */
  const [storedRegions, setStoredRegions] = useState<ReadonlyMap<number, MasterRef>>(new Map());

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const productId = product?.id ?? null;

  const pending = productId === null || settledFor !== productId;

  // The fetch lives in a callback rather than the effect body, matching
  // `ContactsPage`'s reference shape for this repo.
  const load = useCallback((id: number, signal: AbortSignal) => {
    return Promise.all([
      // MASTER read: the filter is opt-in here — omitting it would offer
      // retired rows that are guaranteed 422s on save.
      api.listCampaignInclusions({ isActive: true, perPage: MASTER_PAGE_SIZE }, signal),
      // PRODUCT read: bare, no params — see header, this one 422s on `?isActive=true`.
      api.productInclusions(id, signal),
      /*
       * The REGIONS read, and it is allowed to fail ON ITS OWN.
       *
       * ⚠️ `?isActive=true` because a retired `regionId` is a 422 naming
       * `inclusionIds` — the write is stricter here than it is about the
       * inclusion ids themselves. All six regions are active today, so this
       * filter narrows nothing and omitting it would look entirely correct; it
       * breaks the first time anybody retires one.
       *
       * It is caught rather than joined into the `Promise.all` rejection because
       * losing the region list must not lose the inclusion list: the ticks are
       * still perfectly editable without it, and failing the whole dialog would
       * take a working control away over a second request.
       */
      api
        .listRegions({ isActive: true, perPage: REGION_PAGE_SIZE }, signal)
        .then((response) => response.data)
        .catch(() => null),
    ])
      .then(([masterResponse, offeredResponse, regionRows]) => {
        if (signal.aborted) return;
        setLoadError(null);
        setMaster(masterResponse.data);
        setMasterTotal(masterResponse.meta.total);
        setOffered(offeredResponse.data);
        setSelected(new Set(offeredResponse.data.map((inclusion) => inclusion.id)));

        const read = scopesOf(offeredResponse.data);
        setScopes(read.scopes);
        setLoadedScopes(read.scopes);
        setOfferedNowhere(read.offeredNowhere);
        setStoredRegions(read.seen);
        setRegions(regionRows ?? []);
        setRegionsFailed(regionRows === null);
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        setLoadError(
          error instanceof ApiError
            ? `Couldn’t load the inclusions. ${error.message}`
            : 'Could not reach the server.',
        );
      })
      .finally(() => {
        if (signal.aborted) return;
        // Marks the read as SETTLED either way (what `pending` reads); clears
        // any save error left over from a previous product.
        setFormError(null);
        setConfirmingClear(false);
        setSettledFor(id);
      });
  }, []);

  useEffect(() => {
    // `open` is checked rather than assumed, so an always-mounted call site
    // wouldn't fetch and render a dialog nobody asked for.
    if (!open || productId === null) return;
    const controller = new AbortController();
    void load(productId, controller.signal);
    return () => controller.abort();
  }, [open, productId, load]);

  /** The ids this product offered when the dialog opened — the save comparison. */
  const loadedIds = useMemo(
    () => offered.map((inclusion) => inclusion.id).sort((a, b) => a - b),
    [offered],
  );

  const selectedIds = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);

  /*
   * ⚠️ The comparison covers BOTH halves — which inclusions, and which regions.
   *
   * A regions-only change is a real change, and it is the whole of what this
   * feature adds. Leaving it out would disable Save on the one edit somebody
   * opened the dialog to make, which reads as a broken button rather than as a
   * missing branch. Both region keys are built over `selectedIds`, so an
   * inclusion that has just been unticked cannot keep the form dirty through a
   * scope the PUT will not carry.
   */
  const dirty = useMemo(
    () =>
      loadedIds.length !== selectedIds.length ||
      loadedIds.some((id, index) => id !== selectedIds[index]) ||
      scopeKey(selectedIds, scopes) !== scopeKey(selectedIds, loadedScopes),
    [loadedIds, selectedIds, scopes, loadedScopes],
  );

  /**
   * The region picker's options: active regions, plus any region already
   * recorded against an inclusion that the active list does not carry.
   *
   * ⚠️ **The injection is not optional.** Reads never filter on `isActive` and
   * writes refuse a retired row, so a stored region absent from the options
   * would render as a missing chip; the user would save without it and the offer
   * would silently widen. It is labelled instead, and the save is left to be
   * refused by the server, which names the real id.
   */
  const regionOptions = useMemo(() => {
    const active = new Set(regions.map((region) => region.id));
    const options = regions.map((region) => ({
      value: String(region.id),
      label: region.name,
    }));
    for (const [id, region] of storedRegions) {
      if (active.has(id)) continue;
      options.push({ value: String(id), label: `${region.name} (no longer offered)` });
    }
    return options;
  }, [regions, storedRegions]);

  /*
   * An inclusion this product already offers but that's since been RETIRED
   * (not in the pickable set). Shown separately rather than dropped — since
   * the save is a full replace, dropping it would remove an offer nobody
   * chose to remove.
   */
  const retiredOffered = useMemo(() => {
    const pickable = new Set(master.map((inclusion) => inclusion.id));
    return offered.filter((inclusion) => !pickable.has(inclusion.id));
  }, [master, offered]);

  const masterTruncated = masterTotal > master.length;

  /**
   * The names behind {@link offeredNowhere}, for the warning — and only while
   * those inclusions are still ticked, since unticking one is one of the two
   * ways out of the state.
   *
   * Falls back to the id when the master does not carry the row, rather than
   * dropping the entry: a warning that names nothing is a warning nobody can
   * act on.
   */
  const offeredNowhereShown = useMemo(() => {
    const names: string[] = [];
    for (const id of offeredNowhere) {
      if (!selected.has(id)) continue;
      const row = master.find((candidate) => candidate.id === id) ??
        offered.find((candidate) => candidate.id === id);
      names.push(row?.name ?? `inclusion ${id}`);
    }
    return names;
  }, [offeredNowhere, selected, master, offered]);

  function toggle(id: number, next: boolean) {
    setSelected((current) => {
      const draft = new Set(current);
      if (next) draft.add(id);
      else draft.delete(id);
      return draft;
    });
    /*
     * ⚠️ Unticking deliberately does NOT drop the region scope.
     *
     * `buildOffers` and both `scopeKey` calls iterate the SELECTION, so a scope
     * on an unticked inclusion is already unreachable — it sends nothing and it
     * cannot make the form dirty. Keeping it means unticking and re-ticking by
     * accident restores the regions the person had chosen, rather than silently
     * widening that inclusion to everywhere. The same reasoning as never
     * clearing a hidden block's draft state.
     */
  }

  /**
   * The ONLY writer of a region scope.
   *
   * ⚠️ **An empty selection DELETES the key rather than storing `[]`.** Empty
   * means *offered everywhere* — the picker says so, and a bare id is how the
   * write body spells it — while a stored `[]` would be a second, unwritable
   * spelling of "offered nowhere" sitting in the same field. Closing that here,
   * at the one writer, is what lets every reader below treat "absent" as the
   * whole of the default case.
   */
  function setScope(inclusionId: number, regionIds: readonly number[]) {
    setScopes((current) => {
      const draft = new Map(current);
      if (regionIds.length === 0) draft.delete(inclusionId);
      else draft.set(inclusionId, [...regionIds]);
      return draft;
    });
  }

  async function put(): Promise<void> {
    if (productId === null || product === null) return;

    setSaving(true);
    setFormError(null);
    try {
      /*
       * ⚠️ `inclusionIds` is ALWAYS sent, including when it is empty.
       *
       * The key is required — an absent one is a 422 — and `[]` is the explicit
       * "offer nothing". Two opposite meanings for one falsy value, so this must
       * never become a conditional spread or an `if (ids.length)` guard: the
       * intent that would be dropped is the one the user just confirmed.
       *
       * And it is a FULL REPLACE, not a diff. `selectedIds` is the final set.
       *
       * ⚠️ Each entry is a BARE ID for "offered everywhere" and one
       * `{inclusionId, regionId}` per region otherwise — there is no `null`
       * spelling for "clear the region", so the bare id IS the clear.
       * {@link buildOffers} is the single place that translation happens.
       */
      await api.setProductInclusions(productId, buildOffers(selectedIds, scopes));
      onSaved();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) setFormError(describeSaveFailure(error, product.productName));
      else setFormError('Could not reach the server. Nothing was saved — check your connection.');
    } finally {
      setSaving(false);
      setConfirmingClear(false);
    }
  }

  function handleSave() {
    if (saving || !dirty || !canWrite) return;
    // Emptying the list is legitimate, so it's confirmed rather than blocked.
    if (selectedIds.length === 0 && loadedIds.length > 0) {
      setConfirmingClear(true);
      return;
    }
    void put();
  }

  if (!open || product === null) return null;

  const productName = product.productName;
  const hasMaster = master.length > 0;
  const clearedCount = loadedIds.length;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Campaign inclusions offered"
        // A half-made selection must not vanish on a clipped backdrop click.
        dismissOnBackdrop={false}
        description={canWrite ? 'Saving replaces the whole list.' : (readOnlyReason ?? undefined)}
        footer={
          canWrite ? (
            <>
              <Button variant="secondary" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                // The write is a full replace, so an unchanged PUT would just
                // rewrite the row with what it already holds.
                disabled={pending || Boolean(loadError) || !dirty}
              >
                Save offered inclusions
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )
        }
      >
        <div className="grid gap-4">
          {formError && (
            <Notice tone="danger" role="alert">
              <span className="[overflow-wrap:anywhere]">{formError}</span>
            </Notice>
          )}

          {!canWrite && readOnlyReason && (
            <Notice tone="neutral">
              {readOnlyReason} Changing it is an administrator action.
            </Notice>
          )}

          {/* 1. LOADING — the shape of the list that is coming, not a spinner. */}
          {pending && (
            <div className="grid gap-2" aria-hidden="true">
              {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <div key={index} className="flex gap-2.5 py-1">
                  <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded-xs" />
                  <div className="grid min-w-0 flex-1 gap-1.5">
                    <Skeleton className="h-3.5 w-40 max-w-full" />
                    <Skeleton className="h-3 w-28 max-w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 2. ERROR — names what failed, and offers no half-loaded list. */}
          {!pending && loadError && (
            <Notice tone="danger" role="alert">
              <span className="[overflow-wrap:anywhere]">
                {loadError} Nothing has changed — close this and try again.
              </span>
            </Notice>
          )}

          {/* 3. EMPTY — no inclusions exist to offer, and where to make one. */}
          {!pending && !loadError && !hasMaster && retiredOffered.length === 0 && (
            <EmptyState
              title="No campaign inclusions exist yet"
              description={
                <>Create some under Campaign inclusions on the Catalog screen.</>
              }
            />
          )}

          {!pending && !loadError && (hasMaster || retiredOffered.length > 0) && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="m-0 text-xs text-fg-muted">
                  <span className="font-medium text-fg-secondary">{selectedIds.length}</span> of{' '}
                  {master.length + retiredOffered.length} offered
                </p>
                {canWrite && hasMaster && (
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelected(
                          new Set([
                            ...master.map((inclusion) => inclusion.id),
                            // Retired offers are carried through, not added.
                            ...retiredOffered
                              .filter((inclusion) => selected.has(inclusion.id))
                              .map((inclusion) => inclusion.id),
                          ]),
                        )
                      }
                      disabled={saving}
                    >
                      Offer all
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      // Untick-everything, not a save — an ordinary draft edit.
                      onClick={() => setSelected(new Set())}
                      disabled={saving || selectedIds.length === 0}
                    >
                      Offer none
                    </Button>
                  </div>
                )}
              </div>

              {masterTruncated && (
                <Notice tone="warning">
                  Showing {master.length} of {masterTotal} active campaign inclusions. The rest
                  cannot be ticked here — retire the ones you do not use.
                </Notice>
              )}

              {offeredNowhereShown.length > 0 && (
                <Notice tone="warning" role="alert">
                  {offeredNowhereShown.join(', ')} {offeredNowhereShown.length === 1 ? 'is' : 'are'}{' '}
                  offered in <span className="font-medium">no region</span>, which cannot be saved.
                  Saving now makes {offeredNowhereShown.length === 1 ? 'it' : 'them'} offered
                  everywhere — pick regions, or untick.
                </Notice>
              )}

              {hasMaster && (
                <div
                  role="group"
                  aria-label="Campaign inclusions this product offers"
                  className="grid max-h-[45vh] gap-3 overflow-y-auto rounded-md border border-border p-3"
                >
                  {master.map((inclusion) => (
                    <OfferRow
                      key={inclusion.id}
                      inclusion={inclusion}
                      label={inclusion.name}
                      checked={selected.has(inclusion.id)}
                      disabled={!canWrite || saving}
                      regionIds={scopes.get(inclusion.id)}
                      regionOptions={regionOptions}
                      regionsDisabled={!canWrite || saving || regionsFailed}
                      onToggle={(next) => toggle(inclusion.id, next)}
                      onRegionsChange={(ids) => setScope(inclusion.id, ids)}
                    />
                  ))}
                </div>
              )}

              {retiredOffered.length > 0 && (
                <div className="grid gap-2 rounded-md border border-border bg-surface-sunken p-3">
                  <p className="m-0 text-xs text-fg-muted">
                    Unticking one removes the offer for good — it cannot be ticked again unless the
                    inclusion is made active first.
                  </p>
                  {retiredOffered.map((inclusion) => (
                    <OfferRow
                      key={inclusion.id}
                      inclusion={inclusion}
                      label={`${inclusion.name} (retired)`}
                      checked={selected.has(inclusion.id)}
                      disabled={!canWrite || saving}
                      regionIds={scopes.get(inclusion.id)}
                      regionOptions={regionOptions}
                      regionsDisabled={!canWrite || saving || regionsFailed}
                      onToggle={(next) => toggle(inclusion.id, next)}
                      onRegionsChange={(ids) => setScope(inclusion.id, ids)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmingClear}
        onClose={() => setConfirmingClear(false)}
        onConfirm={() => void put()}
        title="Offer no inclusions on this product?"
        confirmLabel="Save with none offered"
        loading={saving}
        description={
          <>
            <p className="m-0">
              Removes all {clearedCount} {clearedCount === 1 ? 'inclusion' : 'inclusions'} from what{' '}
              {productName} offers.
            </p>
            <p className="m-0 mt-2">
              Existing quote and invoice lines keep theirs. Reversible — tick them back.
            </p>
          </>
        }
      />
    </>
  );
}
